import React, { useEffect, useState, useRef } from "react";
import { Outlet } from "react-router-dom";
import { authService } from "../../../services";
import { useCompany } from "../../../contexts/CompanyContext";
import { ThemeProvider, useTheme } from "../../../contexts/ThemeContext";
import { SidebarButton } from "./SidebarButton";
import { SidebarCadastrosSection } from "./SidebarCadastrosSection";
import { CreateCompanyModal } from "./CreateCompanyModal";
import { CompanyLogoThumb } from "../shared";
import { useToaster, Icons } from "../../shared";

const ThemedAdminPanel: React.FC = () => {
  const { company, companies, setCompany, addCompany, isLoading: companyLoading } = useCompany();
  const { theme, setCompany: setThemeCompany } = useTheme();
  const { showToast } = useToaster();
  const userData = authService.getUserData();
  const userType = userData?.userType;
  const isProfessional = userType === "professional";
  const userName = userData?.name || "Admin";
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const companyDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target as Node)) {
        setCompanyDropdownOpen(false);
      }
    };
    if (companyDropdownOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [companyDropdownOpen]);

  // Dark blue theme: accent blue (icons/active bar), mid blue (active/hover bg)
  const accentBlue = "#63B3ED";
  const midBlue = "#2D3748";
  const primaryColor = accentBlue;
  const primaryColorHover = midBlue;

  useEffect(() => {
    if (company) {
      setThemeCompany(company); // Set theme based on company from context
    }
  }, [company, setThemeCompany]);

  // Apply dark class to document so portaled content (modals, dropdowns) gets dark styles
  useEffect(() => {
    document.documentElement.classList.add("dark");
    return () => document.documentElement.classList.remove("dark");
  }, []);

  const handleLogout = () => {
    authService.logout();
  };

  const getBasePath = () => {
    return "";
  };

  const companyName = company?.name || "Loading...";

  const handleCompanyCreated = (newCompany: { id: number; name: string; isActive: boolean }) => {
    addCompany(newCompany);
    setCompany(newCompany);
    setShowCreateCompanyModal(false);
    setCompanyDropdownOpen(false);
    showToast("success", "Empresa criada com sucesso!");
  };


  // Show loading while company data is being loaded
  if (companyLoading) {
    return (
      <div className="min-h-screen bg-slate-700 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-400 mx-auto"></div>
          <p className="mt-4 text-slate-300">Carregando dados da empresa...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-slate-700">
      {/* Navbar - fixed at top, does not scroll */}
      <header ref={headerRef} className="flex-shrink-0 bg-[#1A202C] border-b border-[#2D3748] shadow-sm">
        <div className="px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0 flex-1">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-gray-300 hover:text-white hover:bg-[#2D3748] rounded-lg transition-colors duration-200 flex-shrink-0"
                aria-label="Toggle menu"
              >
                <span className="text-xl">{isMobileMenuOpen ? "✕" : "☰"}</span>
              </button>

              <div className="flex items-center gap-2 sm:gap-3 min-w-0" ref={companyDropdownRef}>
                <div className="relative min-w-0">
                  <button
                    type="button"
                    onClick={() => setCompanyDropdownOpen((o) => !o)}
                    className="flex items-center gap-2 ml-0 sm:ml-2 px-2 py-1.5 sm:py-1 rounded-lg bg-[#2D3748] hover:bg-[#374151] text-white text-sm font-medium border border-[#2D3748] min-w-0 max-w-[220px] sm:max-w-[300px] text-left transition-colors"
                    title="Trocar empresa"
                  >
                    {company ? <CompanyLogoThumb company={company} size="sm" /> : null}
                    <span className="truncate min-w-0 flex-1">{companyName}</span>
                    <span className={`flex-shrink-0 text-[#A0AEC0] transition-transform ${companyDropdownOpen ? "rotate-180" : ""}`}>
                      ▼
                    </span>
                  </button>
                  {companyDropdownOpen && (
                    <div className="absolute left-0 top-full mt-1 w-64 max-h-64 overflow-auto bg-white dark:bg-[#1A202C] rounded-xl shadow-lg border border-gray-200 dark:border-[#374151] py-1 z-50">
                      {companies.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            setCompany(c);
                            setCompanyDropdownOpen(false);
                          }}
                          className={`w-full px-3 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-100 dark:hover:bg-[#2D3748] ${
                            company?.id === c.id
                              ? "bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-200 font-medium"
                              : "text-gray-900 dark:text-slate-100"
                          }`}
                        >
                          <CompanyLogoThumb company={c} size="sm" />
                          <span className="truncate min-w-0">{c.name}</span>
                        </button>
                      ))}
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            setShowCreateCompanyModal(true);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-[#63B3ED] hover:bg-sky-50 font-medium flex items-center gap-2"
                        >
                          <span>+</span>
                          Nova empresa
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <div className="hidden sm:flex flex-col items-end text-sm">
                <span className="font-semibold text-white">
                  Bem-vindo, {userName}
                </span>
              </div>

              {/* Logout Button */}
              <button
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg font-medium transition-all duration-200 text-red-400 hover:bg-red-500/20 hover:text-red-300 border border-red-500/40 hover:border-red-400 text-xs sm:text-sm"
                onClick={handleLogout}
              >
                <span>Sair</span>
                <span className="hidden sm:inline text-xs">▼</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <CreateCompanyModal
        isOpen={showCreateCompanyModal}
        onClose={() => setShowCreateCompanyModal(false)}
        onSuccess={handleCompanyCreated}
      />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar Navigation - fixed height, does not scroll with page */}
        <nav
          className={`flex flex-col flex-shrink-0 ${
            isSidebarMinimized && !isMobileMenuOpen ? "w-16" : "w-64"
          } ${
            isMobileMenuOpen
              ? "fixed left-0 top-14 bottom-0 z-50 lg:relative lg:top-auto lg:bottom-auto"
              : "hidden lg:block"
          } h-full bg-[#1A202C] border-r border-[#2D3748] shadow-sm transition-all duration-300`}
        >
          {/* Minimize/Maximize Button - fixed at top of sidebar, only on desktop */}
          <div className="flex-shrink-0 relative hidden lg:block">
            <button
              onClick={() => setIsSidebarMinimized(!isSidebarMinimized)}
              className="absolute -right-3 top-6 z-20 flex w-6 h-6 bg-[#2D3748] border border-[#374151] rounded-full shadow-md items-center justify-center text-gray-300 hover:text-white hover:bg-[#374151] hover:shadow-lg transition-all duration-200"
              title={isSidebarMinimized ? "Expandir menu" : "Minimizar menu"}
            >
              <span className="text-xs font-bold">
                {isSidebarMinimized ? "→" : "←"}
              </span>
            </button>
          </div>

          <div
            className={`flex-1 min-h-0 overflow-y-auto ${
              isSidebarMinimized && !isMobileMenuOpen ? "p-2 pt-6" : "p-4 sm:p-6"
            } transition-all duration-300`}
          >
            <div className="space-y-2">
            <SidebarButton
                icon={<Icons.Transactions />}
                label="Lançamentos"
                path="/transactions"
                isMinimized={isSidebarMinimized && !isMobileMenuOpen}
                primaryColor={primaryColor}
                primaryColorHover={primaryColorHover}
                getBasePath={getBasePath}
                onNavigate={() => setIsMobileMenuOpen(false)}
              />
              <SidebarButton
                icon={<Icons.Dashboard />}
                label="Demonstrativo"
                path="/management-report"
                isMinimized={isSidebarMinimized && !isMobileMenuOpen}
                primaryColor={primaryColor}
                primaryColorHover={primaryColorHover}
                getBasePath={getBasePath}
                onNavigate={() => setIsMobileMenuOpen(false)}
              />
              <SidebarButton
                icon={<Icons.Calendar />}
                label="Calendário"
                path="/payment-calendar"
                isMinimized={isSidebarMinimized && !isMobileMenuOpen}
                primaryColor={primaryColor}
                primaryColorHover={primaryColorHover}
                getBasePath={getBasePath}
                onNavigate={() => setIsMobileMenuOpen(false)}
              />
              <SidebarCadastrosSection
                isMinimized={isSidebarMinimized && !isMobileMenuOpen}
                primaryColor={primaryColor}
                primaryColorHover={primaryColorHover}
                getBasePath={getBasePath}
                onNavigate={() => setIsMobileMenuOpen(false)}
              />
              <SidebarButton
                icon={<Icons.Company />}
                label="Empresa"
                path="/company"
                isMinimized={isSidebarMinimized && !isMobileMenuOpen}
                primaryColor={primaryColor}
                primaryColorHover={primaryColorHover}
                getBasePath={getBasePath}
                onNavigate={() => setIsMobileMenuOpen(false)}
              />
              <SidebarButton
                icon={<Icons.Import />}
                label="Importações"
                path="/imports"
                isMinimized={isSidebarMinimized && !isMobileMenuOpen}
                primaryColor={primaryColor}
                primaryColorHover={primaryColorHover}
                getBasePath={getBasePath}
                onNavigate={() => setIsMobileMenuOpen(false)}
              />
            </div>
          </div>
        </nav>

        {/* Main Content - only this area scrolls; dark class enables dark mode for cards/lists */}
        <main className="dark flex-1 min-h-0 p-4 sm:p-6 md:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

// Main component with ThemeProvider wrapper
export const AdminPanel: React.FC = () => {
  return (
    <ThemeProvider>
      <ThemedAdminPanel />
    </ThemeProvider>
  );
};
